'use client';

import { FC, ReactElement, useEffect, useEffectEvent, useState } from 'react';

import type { FlagExperiment } from '@/data/FlagsApi/FlagsApi.type';
import { getGroupForExperiment } from '@/general/helpers/flags/getGroupForExperiment';

import { useABTestEvent } from './useABTestEvent';

type RendererProps = {
  experiment: FlagExperiment;
  controlComponent: ReactElement;
  variantComponent: ReactElement;
  LoadingComponent?: ReactElement;
  /** If true, the impression event will not be tracked when the component mounts */
  noImpressionEvent?: boolean;
};

type Props = Omit<RendererProps, 'experiment'> & {
  experiment: FlagExperiment | null;
};

export const ABTest: FC<Props> = ({ experiment, ...props }) => {
  if (!experiment) {
    console.warn(`Experiment is not configured. Defaulting to baseline component`);
    return props.controlComponent;
  }

  return <ABRestRenderer {...props} experiment={experiment} />;
};

const ABRestRenderer: FC<RendererProps> = ({
  experiment,
  controlComponent,
  variantComponent,
  LoadingComponent,
  noImpressionEvent,
}) => {
  const [loaded, setLoaded] = useState(false);
  const group = getGroupForExperiment(experiment);

  const { triggerEvent } = useABTestEvent(experiment, 'impression', { enabled: false });

  const onLoaded = useEffectEvent(() => {
    setLoaded(true);
    if (!noImpressionEvent) {
      triggerEvent();
    }
  });

  useEffect(() => {
    onLoaded();
  }, []);

  if (!loaded && LoadingComponent) {
    return LoadingComponent;
  }

  return group === 'a' || !loaded ? controlComponent : variantComponent;
};
